import type IMapProperty from './IMapProperty';
import type BaseDto from '../interface/IBaseDto';

type ObjectType = Record<string, unknown>;

function _isEntity<Entity extends ObjectType>(object: unknown): object is BaseDto<Entity> {
    if (!object || typeof object !== 'object') {
        return false;
    }

    const record: Partial<BaseDto<Entity>> = object as Record<string, unknown>;

    return (
        typeof record.flatten === 'function' &&
        typeof record.apply === 'function' &&
        typeof record.mappings === 'function'
    );
}

function _mapExtToIntProps<IntProps extends ObjectType, ExtProps extends ObjectType>(
    mappings: Array<IMapProperty>,
    sourceObject?: Partial<ExtProps>,
): IntProps {
    const props: ObjectType = {};

    for (const property of mappings) {
        let newValue = null;
        const externalName = property.externalName ?? property.localName;

        if (!sourceObject) {
            /* ignore */
        } else if (property.removeValueOnImport ?? false) {
            /* ignore */
        } else if (property.customImport) {
            newValue = property.customImport(sourceObject);
        } else if (sourceObject[externalName] !== undefined) {
            newValue = sourceObject[externalName] as typeof property.defaultValue;
        } else if (property.defaultValue != null && !newValue) {
            newValue = property.defaultValue as typeof property.defaultValue;
        }

        if (newValue !== null && newValue !== undefined) {
            props[property.localName] = newValue;
        }
    }

    return props as IntProps;
}

/** IntProps = internal props, the consumer api, ExtProps = the external workspace api, CustomProps = linkables */
function create<
    IntProps extends ObjectType,
    ExtProps extends ObjectType,
    CustomProps extends Object,
>(
    mappings: Array<IMapProperty>,
    customProps: CustomProps,
    sourceObject?: Partial<ExtProps>,
): BaseDto<ExtProps> & IntProps & CustomProps {
    const newEntity: BaseDto<ExtProps> & IntProps & CustomProps = {
        /* BaseDto fields */
        apply: (data: ObjectType) => {
            return apply(mappings, customProps, newEntity);
        },

        flatten: () => {
            return flatten(newEntity, mappings);
        },

        mappings: () => {
            return mappings;
        },

        /* convert Ext(external field definitions) to local field definitions */
        ..._mapExtToIntProps<IntProps, ExtProps>(mappings, sourceObject),

        /* any custom fields passed in (will override above) */
        ...customProps,
    };

    return newEntity;
}

/** take an incoming IMapProperty[] and generate a new object with the sourceObject  */
function apply<
    IntProps extends ObjectType,
    ExtProps extends ObjectType,
    CustomProps extends Object,
>(
    mappings: Array<IMapProperty>,
    customProps: CustomProps,
    sourceObject: ExtProps | BaseDto<ExtProps>,
): BaseDto<ExtProps> & IntProps & CustomProps {
    const newEntity = create<IntProps, ExtProps, CustomProps>(
        mappings,
        customProps,
        /* if we are given an existing entity, flatten it out & apply that */
        _isEntity<ExtProps>(sourceObject)
            ? sourceObject.flatten()
            : (sourceObject as ExtProps) ?? {},
    );

    return newEntity;
}

function flatten<Api extends ObjectType>(
    entityIn: BaseDto<Api>,
    mappings?: Array<IMapProperty>,
): Api {
    const flattenObject: ObjectType = {};

    const entityCast: ObjectType = entityIn as ObjectType;

    for (const property of mappings ?? []) {
        let value = property.defaultValue;
        const keySave = property.externalName ?? property.localName;
        const keyGet = property.localName;

        if (property.removeValueOnExport ?? false) {
            /* ignore */
            continue;
        } else if (property.customExport) {
            value = property.customExport(entityCast);
        } else if (entityCast[keyGet] !== undefined) {
            value = entityCast[keyGet];
        }

        flattenObject[keySave] = value;
    }

    return flattenObject as Api;
}

export default {
    create,
    apply,
    flatten,
};
